/**
 * Format a timestamp into human-readable relative time (e.g. "just now", "5m ago", "2h ago", "3d ago").
 * Supports English, Arabic (and Darija), French, Spanish, and German.
 *
 * @param {string|Date|number} date - Timestamp to format
 * @param {string} [lang='en'] - Active language code (e.g. 'en', 'ar', 'ar-MA', 'fr', 'es', 'de')
 * @returns {string} Relative time string
 */
export function formatTimeAgo(date, lang = 'en') {
  if (!date) return '';
  const now = Date.now();
  const past = new Date(date).getTime();
  if (isNaN(past)) return '';
  const diffSec = Math.max(0, Math.floor((now - past) / 1000));

  const isAr = lang?.startsWith('ar');
  const isFr = lang?.startsWith('fr');
  const isEs = lang?.startsWith('es');
  const isDe = lang?.startsWith('de');

  if (diffSec < 45) {
    if (isAr) return 'الآن';
    if (isFr) return "à l'instant";
    if (isEs) return 'ahora';
    if (isDe) return 'gerade eben';
    return 'just now';
  }

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    if (isAr) return `${diffMin} د`;
    if (isFr) return `${diffMin} min`;
    if (isEs) return `${diffMin} min`;
    if (isDe) return `${diffMin} Min.`;
    return `${diffMin}m`;
  }

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) {
    if (isAr) return `${diffHour} س`;
    if (isFr) return `${diffHour} h`;
    if (isEs) return `${diffHour} h`;
    if (isDe) return `${diffHour} Std.`;
    return `${diffHour}h`;
  }

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) {
    if (isAr) return `${diffDay} ي`;
    if (isFr) return `${diffDay} j`;
    if (isEs) return `${diffDay} d`;
    if (isDe) return `${diffDay} T.`;
    return `${diffDay}d`;
  }

  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) {
    if (isAr) return `${diffWeek} أسبوع`;
    if (isFr) return `${diffWeek} sem.`;
    if (isEs) return `${diffWeek} sem.`;
    if (isDe) return `${diffWeek} W.`;
    return `${diffWeek}w`;
  }

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) {
    if (isAr) return `${diffMonth} شهر`;
    if (isFr) return `${diffMonth} mois`;
    if (isEs) return `${diffMonth} m`;
    if (isDe) return `${diffMonth} M.`;
    return `${diffMonth}mo`;
  }

  const diffYear = Math.floor(diffDay / 365);
  if (isAr) return `${diffYear} سنة`;
  if (isFr) return `${diffYear} an${diffYear > 1 ? 's' : ''}`;
  if (isEs) return `${diffYear} a`;
  if (isDe) return `${diffYear} J.`;
  return `${diffYear}y`;
}

export default formatTimeAgo;
